import { useCallback, useEffect, useRef } from 'react';
import { useAppDispatch, useAppSelector } from 'app/store';
import { selectAgaveSlice, migrateERPEndpointSettings } from 'app/pages/store/agaveSlice';
import { selectUserCompanies } from 'app/store/userCompaniesSlice';
import { captureException } from '@sentry/react';
import { AgaveSourceSystem } from 'app/services/agave/agaveConstants';

/**
 * Custom hook that provides a utility function to check if the current agave source system
 * matches any of the provided source systems.
 *
 * Prefers paidol.agaveSourceSystemSlug (instant) over agaveAccount.source_system.slug (async)
 * for faster ERP type detection.
 *
 * @example
 * const { sourceSystemIn, isQBOClient, isSage300Client } = useAgaveSourceSystem();
 *
 * if (sourceSystemIn(AgaveSourceSystem.SAGE_300_CRE, AgaveSourceSystem.SAGE_100_CONTRACTOR)) {
 *   // Handle Sage systems
 * }
 */
export const useAgaveSourceSystem = () => {
  const { agaveAccount } = useAppSelector(selectAgaveSlice);
  const { selectedPaidol: paidol } = useAppSelector(selectUserCompanies);

  // Current source system slug - prefers paidol field, falls back to agaveAccount
  const currentSourceSystem = paidol?.agaveSourceSystemSlug ?? agaveAccount?.source_system?.slug;

  /**
   * Check if the current agave source system matches any of the provided source systems.
   * Prefers paidol field (instant) over agaveAccount (async) for legacy client compatibility.
   * @param sourceSystems - Variable number of source system values to check against
   * @returns boolean - true if the current source system matches any of the provided systems
   */
  const sourceSystemIn = useCallback(
    (...sourceSystems: string[]): boolean => {
      return currentSourceSystem ? sourceSystems.includes(currentSourceSystem) : false;
    },
    [currentSourceSystem]
  );

  // Pre-computed common ERP type checks for convenience
  const isQBDClient = sourceSystemIn(AgaveSourceSystem.QUICKBOOKS_DESKTOP);
  const isQBOClient = sourceSystemIn(AgaveSourceSystem.QUICKBOOKS_ONLINE);
  const isSage300Client = sourceSystemIn(AgaveSourceSystem.SAGE_300_CRE);
  const isSageIntacctClient = sourceSystemIn(AgaveSourceSystem.SAGE_INTACCT);
  const isAcumaticaClient = sourceSystemIn(AgaveSourceSystem.ACUMATICA);

  return {
    sourceSystemIn,
    currentSourceSystem,
    isQBDClient,
    isQBOClient,
    isSage300Client,
    isSageIntacctClient,
    isAcumaticaClient,
  };
};

/**
 * Custom hook that handles ERP endpoint settings migration for Agave clients.
 *
 * This hook should be called once at the app settings level to trigger lazy migration
 * of erpProjectsEndpointSetting for Agave-enabled paidols.
 *
 * The migration is idempotent and will only update paidols that need it.
 *
 * @example
 * // In AppSettings.tsx
 * useAgaveERPEndpointMigration();
 */
export const useAgaveCheckForAgaveMigration = () => {
  const dispatch = useAppDispatch();
  const { selectedPaidol: paidol } = useAppSelector(selectUserCompanies);
  const { isQBOClient } = useAgaveSourceSystem();
  const migrationCheck = useRef(false);

  useEffect(() => {
    // Only trigger ERP endpoint settings migration for QBO clients
    if (isQBOClient && paidol?.id && !migrationCheck.current) {
      migrationCheck.current = true;

      // Dispatch migration thunk - this will only migrate if needed
      dispatch(migrateERPEndpointSettings({ input: paidol.id }))
        .unwrap()
        .catch((error) => captureException(error));
    }
  }, [isQBOClient, paidol?.id, dispatch]);
};
